'use client';

/**
 * Confirmation of an edit (plan §16.2): an edit that stales a lot of paid
 * work shows the estimate before it is applied; deleting a block with a
 * parent recording, deleting a story with translations, deleting a hero and
 * a paid redraw ask first. The dialog lists what goes stale, what is new and
 * what goes, the credits of «обновить», and the notices (a publication
 * becomes a new version for moderation, translations are marked).
 */
import { useTranslations } from 'next-intl';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import type { EditPlan } from '../types';

/** The plan needs the dialog before it is applied. */
export const needsConfirm = (plan: EditPlan) => !plan.forbiddenReason && plan.confirmRequired.length > 0;

export interface EditConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: EditPlan;
  onConfirm: () => void;
  pending?: boolean;
}

const ESTIMATE_ROWS = ['pages', 'covers', 'sheets', 'audioBlocks', 'assemblies'] as const;

export function EditConfirmDialog({ open, onOpenChange, plan, onConfirm, pending }: EditConfirmDialogProps) {
  const t = useTranslations('stories.edits');
  const e = plan.estimate;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="edit-confirm-dialog" data-kind={plan.kind}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('confirm.title', { edit: t(`kinds.${plan.kind}`) })}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              {plan.confirmRequired.map((r) => (
                <p key={r} data-testid={`edit-confirm-reason-${r}`}>{t(`confirm.reasons.${r}`)}</p>
              ))}
              <p data-testid="edit-confirm-counts">
                {t('confirm.counts', { stale: plan.stale.length, fresh: plan.new.length, gone: plan.gone.length })}
              </p>
              {e ? (
                <ul className="list-disc pl-5" data-testid="edit-confirm-estimate">
                  {ESTIMATE_ROWS.filter((k) => e[k] > 0).map((k) => (
                    <li key={k}>{t(`estimate.${k}`, { count: e[k] })}</li>
                  ))}
                  {e.credits > 0 ? <li className="font-medium" data-testid="edit-confirm-credits">{t('estimate.credits', { credits: e.credits })}</li> : null}
                </ul>
              ) : null}
              {plan.reused.length ? <p data-testid="edit-confirm-reused">{t('confirm.reused', { count: plan.reused.length })}</p> : null}
              {plan.notices.map((n) => (
                <p key={n} className="text-muted-foreground" data-testid={`edit-confirm-notice-${n}`}>{t(`notices.${n}`)}</p>
              ))}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending} data-testid="edit-confirm-cancel">{t('confirm.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending || !!plan.forbiddenReason}
            data-testid="edit-confirm-apply"
            onClick={(ev) => { ev.preventDefault(); onConfirm(); }}
          >
            {t('confirm.apply')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
